// Public, chrome-less render of a single share-card at REAL print size
// (1080x1920 for Story / 1080x1080 for Post). Used by the backend PNG
// generator (Puppeteer screenshots the [data-card] element).
//
// URL params (preferred — used by backend):
//   format     story | post
//   data       base64-encoded JSON `{ campaign, total, winner, top, config }`
//
// Fallback (manual debugging):
//   campaign   campaign id; will fetch /api/campaign/active when missing
//   config     base64-encoded share-card config

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { api } from "../lib/api";
import { StoryCard, PostCard } from "./ShareCardPreview";
import { SC_DEFAULT_CONFIG } from "../lib/shareCardConfig";

function b64decode(raw) {
  if (!raw) return null;
  try {
    const bin = atob(raw.replace(/-/g, "+").replace(/_/g, "/"));
    // atob returns a binary (Latin-1) string — decode as UTF-8 so Polish
    // characters (ł, ą, ę, etc.) survive intact.
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const json = new TextDecoder("utf-8").decode(bytes);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export default function RenderShareCard() {
  const [params] = useSearchParams();
  const format = params.get("format") === "post" ? "post" : "story";
  const injected = useMemo(() => b64decode(params.get("data")), [params]);
  const cfgOverride = useMemo(() => b64decode(params.get("config")), [params]);
  const campaignId = params.get("campaign") || "";

  const [data, setData] = useState(injected || null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    document.body.style.margin = "0";
  }, []);

  useEffect(() => {
    if (injected) return; // already supplied
    api
      .get(campaignId ? `/campaigns/${campaignId}/results` : `/campaign/active`)
      .then(({ data: d }) => {
        const c = d.campaign;
        const board = d.leaderboard || [];
        const w = board[0];
        if (!c || !w) {
          setData({ empty: true });
          return;
        }
        setData({
          campaign: c,
          total: d.total_votes || 0,
          winner: {
            city: w.city,
            skatepark: w.skatepark || "",
            region: w.region || "",
            votes: w.votes,
            pct: w.percentage,
          },
          top: board.slice(1, 5).map((r) => ({
            city: r.city,
            skatepark: r.skatepark || "",
            region: r.region || "",
            votes: r.votes,
            pct: r.percentage,
          })),
        });
      })
      .catch(() => setData({ empty: true }));
  }, [injected, campaignId]);

  // Mark ready when data + fonts loaded (signal for Puppeteer)
  useEffect(() => {
    if (!data) return;
    const finish = () => {
      setReady(true);
      document.body.setAttribute("data-render-ready", "1");
    };
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(finish).catch(finish);
    } else {
      finish();
    }
  }, [data]);

  if (!data) return <div style={{ width: 1, height: 1 }} />;

  if (data.empty) {
    return (
      <div
        data-card
        style={{
          width: 1080,
          height: format === "story" ? 1920 : 1080,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
          fontFamily: "sans-serif",
          fontSize: 32,
          color: "#999",
        }}
      >
        Brak danych
      </div>
    );
  }

  const config = {
    ...SC_DEFAULT_CONFIG,
    ...(data.config || {}),
    ...(cfgOverride || {}),
  };
  const baseW = 540;
  const baseH = format === "story" ? 960 : 540;
  const targetW = 1080;
  const targetH = format === "story" ? 1920 : 1080;
  const scale = targetW / baseW; // = 2

  return (
    <div
      data-card
      data-ready={ready ? "1" : "0"}
      style={{
        width: targetW,
        height: targetH,
        overflow: "hidden",
        position: "relative",
        background: "transparent",
      }}
    >
      <div
        style={{
          width: baseW,
          height: baseH,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        {format === "story" ? (
          <StoryCard data={data} config={config} />
        ) : (
          <PostCard data={data} config={config} />
        )}
      </div>
    </div>
  );
}
